import React, { PureComponent } from 'react';
import { Field, reduxForm, change, formValueSelector, initialize } from 'redux-form';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Select from 'components/forms/Select';
import Button from 'components/buttons/Button';
import PropTypes from 'prop-types';
import MinusIcon from 'mdi-react/MinusIcon';
import { uniqBy, isEmpty, memoize } from 'lodash';
import LoadingOverlay from 'components/LoadingOverlay';
import { Can, AbilityContext } from 'utils/permissions';
import Error from 'components/forms/Error';
import { resultsToOptions } from 'utils/misc';
import RenderFormInput from 'components/forms/RenderFormInput';
import { getUsers } from 'redux/actions/userActions';
import { getAccountEmailConfigs } from 'redux/actions/emailConfigActions';
import { getAccounts } from 'redux/actions/accountActions';
import RenderSelectInput from 'components/forms/RenderSelectInput';
import accountEmailConfigsSelector from 'selectors/accountEmailConfigsSelector';
import campaignEmailConfigSelector from 'selectors/campaignEmailConfigSelector';
import { MIN_DELAY_BETWEEN_EMAILS } from 'constants/config';
import { withStyles } from '@material-ui/styles';
import { Grid, DialogActions } from '@material-ui/core';
import { requiredValidator, requiredOptionValidator, handleBlur } from 'utils/validators';
import { withRouter } from 'react-router';
import { setAddEmailConfigOpen } from 'redux/actions/campaignActions';
import { urlAccountById } from 'constants/apiRouting';
import { getCampaignManagerOption } from 'utils/campaign';
import classNames from 'classnames';
import Counter from 'components/Counter';
import PlusIcon from 'mdi-react/PlusIcon';


const selector = formValueSelector('settingsForm');

const styles = theme => ({
  settingsRows: {
    width: '100%',
  },
  sendingEmailSubTitle: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 'bold',
    marginBottom: '0.75rem',
    color: theme.palette.secondary['900'],
  },
  addEmailWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    gap: '0.75rem',
  },
  deleteEmailButton: {
    visibility: 'hidden',
    opacity: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    padding: '0',
    borderRadius: '50%',
    background: theme.palette.grey['600'],
    color: theme.palette.white,
    border: 'none',
    position: 'absolute',
    top: '-0.625rem',
    right: '-0.625rem',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: theme.palette.delete.contrastText,
      cursor: 'pointer',
    },
  },
  emailServiceProviderGroup: {
    position: 'relative',
    '&:hover $deleteEmailButton': {
      visibility: 'visible',
      opacity: '1',
    },
  },
});

class SettingsForm extends PureComponent {
  static propTypes = {
    getUsers: PropTypes.func.isRequired,
    getAccounts: PropTypes.func.isRequired,
    getAccountEmailConfigs: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    setAddEmailConfigOpen: PropTypes.func.isRequired,
    emailConfigs: PropTypes.shape({}).isRequired,
    campaign: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      email_service_provider_configs: PropTypes.array,
    }).isRequired,
    toggleModal: PropTypes.func,
    accountEmailConfigs: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    formattedEmailConfigs: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    accountFormValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      }),
    ]),
    emailConfigsFormValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      }),
    ]),
    users: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    accounts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    pristine: PropTypes.bool,
    classes: PropTypes.shape({}).isRequired,
  };

  static defaultProps = {
    accountFormValue: {},
    emailConfigsFormValue: {},
    toggleModal: null,
    submitting: false,
    pristine: true,
  };

  constructor(props) {
    super(props);
    const { formattedEmailConfigs, campaign } = props;
    this.state = {
      isInitialLoading: true, // handles showing loader during initial accounts loading
      emailConfigOptions: uniqBy(
        [
          ...resultsToOptions(formattedEmailConfigs, 'formattedEmail'),
          ...resultsToOptions(
            campaignEmailConfigSelector(campaign.email_service_provider_configs),
            'formattedEmail',
          ),
        ],
        'value',
      ) || [],
    };
  }

  getMaxEmailAllowedPerDay(emailConfig) {
    const { accountEmailConfigs } = this.props;
    const selectedEmailConfig = accountEmailConfigs.results && accountEmailConfigs.results.length
    && emailConfig.value !== undefined
      ? accountEmailConfigs.results.find(email => email.id === emailConfig.value)
      : {};
    return (selectedEmailConfig && selectedEmailConfig.total_number_of_emails_per_day) || 0;
  }

  campaignMaxEmailsPerDayValidator = memoize(emailConfig => (value) => {
    const selectedEmailConfig = this.getMaxEmailAllowedPerDay(emailConfig);
    const defaultValue = value || 0;
    return defaultValue > selectedEmailConfig ? `Must be ${selectedEmailConfig} or less` : undefined;
  });

  async handleDefaultData() {
    const { campaign } = this.props;

    const userPromise = this.context.can('see', 'Manager') ?
      [this.props.getUsers({ groups__name: 'Campaign Manager,Campaign Director' })] : [];

    // get items from BE
    await Promise.all([
      this.props.getAccountEmailConfigs(campaign.account),
      ...userPromise,
    ]);

    // this allows to get current values of props after promises and benefits code readability
    const {
      users: { results: usersArray },
    } = this.props;

    const user = !isEmpty(usersArray) && usersArray.find(u => u.pk === campaign.campaign_manager);

    const initialValues = {
      // here we will make an assumption that campaign object is fetched by CampaignDetail component
      name: campaign.name,
      account: { value: campaign.account, label: campaign.account_name },
      campaign_manager: user
        ? getCampaignManagerOption(user)
        : '',
      email_service_provider_configs: isEmpty(campaign.email_service_provider_configs)
        ? this.state.emailConfigOptions.map(emailConf => ({ label: emailConf.label, value: emailConf.value }))
        : resultsToOptions(
          campaignEmailConfigSelector(campaign.email_service_provider_configs),
          'formattedEmail',
        ),
    };

    // set values as pristine to be able to detect changes
    this.props.dispatch(initialize(
      'settingsForm',
      initialValues,
    ));
    campaign.email_service_provider_configs.forEach((emailConf) => {
      this.props.dispatch(change(
        'settingsForm',
        `${emailConf.id}.total_number_of_emails_per_day`,
        emailConf.total_number_of_emails_per_day,
      ));
      this.props.dispatch(change(
        'settingsForm',
        `${emailConf.id}.delay_between_each_email_sent`,
        emailConf.delay_between_each_email_sent,
      ));
    });
  }

  handleGetOptions = async (accountString) => {
    // will also fire on initial rendering
    const { isInitialLoading } = this.state;
    if (isInitialLoading) {
      await this.props.getAccounts({ search: accountString, status: true });
      // populating default data
      await this.handleDefaultData();
      this.setState({ isInitialLoading: false });
    } else {
      await this.props.getAccounts({ search: accountString, status: true }, true);
    }
    return resultsToOptions(this.props.accounts.results, 'name') || [];
  }

  handleAccountChange = async (account) => {
    // notify redux-form about values change
    this.props.dispatch(change('settingsForm', 'account', account));

    // by default (during onChange) form values will be without selected items
    this.props.dispatch(change('settingsForm', 'email_service_provider_configs', []));

    // get Email configs from BE
    await this.props.getAccountEmailConfigs(account.value);

    // this allows to get current values of props after promises and benefits code readability
    const {
      formattedEmailConfigs,
    } = this.props;

    // handle emailConfig
    if (formattedEmailConfigs.length === 1) {
      // handle single emailConfig being selected
      this.props.dispatch(change(
        'settingsForm',
        'email_service_provider_configs',
        [resultsToOptions(formattedEmailConfigs, 'formattedEmail')[0]],
      ));
    }
  }

  handleEmailConfigAdd = () => {
    const {
      dispatch, formattedEmailConfigs, accountEmailConfigs, emailConfigsFormValue,
    } = this.props;

    const chosenEmailConfigIds = !isEmpty(emailConfigsFormValue) ? [
      ...emailConfigsFormValue.map(config => config.value),
    ] : [];
    const emailConfigToAdd = formattedEmailConfigs.find(config => !chosenEmailConfigIds.includes(config.id));
    const emailConfig = accountEmailConfigs.results.find(config => config.id === emailConfigToAdd.id);
    const emailConfigWithValues = resultsToOptions([emailConfigToAdd], 'formattedEmail')[0];

    dispatch(change(
      'settingsForm',
      'email_service_provider_configs',
      [...emailConfigsFormValue, emailConfigWithValues],
    ));
    dispatch(change(
      'settingsForm',
      `${emailConfig.id}.total_number_of_emails_per_day`,
      emailConfig.total_number_of_emails_per_day,
    ));
    dispatch(change(
      'settingsForm',
      `${emailConfig.id}.delay_between_each_email_sent`,
      emailConfig.delay_between_each_email_sent,
    ));
  }

  handleEmailConfigChange = index => (selectedEmailConfig) => {
    const {
      dispatch,
      accountEmailConfigs,
      emailConfigsFormValue,
    } = this.props;
    const emailConfig = accountEmailConfigs.results.find(config => config.id === selectedEmailConfig.value);
    const newConfigs = [...emailConfigsFormValue];
    newConfigs[index] = { ...selectedEmailConfig };
    dispatch(change(
      'settingsForm',
      'email_service_provider_configs',
      newConfigs,
    ));
    dispatch(change(
      'settingsForm',
      `${emailConfig.id}.total_number_of_emails_per_day`,
      emailConfig.total_number_of_emails_per_day,
    ));
    dispatch(change(
      'settingsForm',
      `${emailConfig.id}.delay_between_each_email_sent`,
      emailConfig.delay_between_each_email_sent,
    ));
  }

  handleEmailConfigRemove = (index) => {
    const { dispatch, emailConfigsFormValue } = this.props;
    const emailConfigs = [...emailConfigsFormValue];
    emailConfigs.splice(index, 1);
    dispatch(change(
      'settingsForm',
      'email_service_provider_configs',
      emailConfigs,
    ));
  }

  async handleEmailConfigCreate() {
    this.props.setAddEmailConfigOpen(true);
    this.props.history.push(urlAccountById(this.props.accountFormValue.value));
  }

  render() {
    let emailConfigPlaceholder = 'Select email configuration...';
    const { isInitialLoading } = this.state;
    const {
      toggleModal,
      accountEmailConfigs,
      emailConfigsFormValue,
      accountFormValue,
      handleSubmit,
      submitting,
      pristine,
      users,
      classes,
      formattedEmailConfigs,
    } = this.props;

    const isEmailConfigsLoading = accountEmailConfigs.loading;
    const isManagersLoading = users.loading;
    const isFormLoading = isInitialLoading;

    const hasEmailConfig = accountEmailConfigs.results ? accountEmailConfigs.results.length > 0 : false;

    if (accountFormValue) {
      if (isEmailConfigsLoading) {
        emailConfigPlaceholder = 'Loading...';
      } else if (!hasEmailConfig) {
        emailConfigPlaceholder = 'No email configuration set. Please do so in the account settings';
      }
    } else {
      emailConfigPlaceholder = 'First select an account';
    }

    const campaignManagerOptions = users.results &&
      users.results.map(user => (getCampaignManagerOption(user)));

    const canRemoveEmailConfig = emailConfigsFormValue.length > 1;


    const chosenEmailConfigIds = !isEmpty(emailConfigsFormValue) ? [
      ...emailConfigsFormValue.map(config => config.value),
    ] : [];

    const availableEmailConfigs = resultsToOptions(
      formattedEmailConfigs.filter(emailConfig => !chosenEmailConfigIds.includes(emailConfig.id)),
      'formattedEmail',
    ) || [];

    const disabledEmailConfigAdd = emailConfigsFormValue.length === formattedEmailConfigs.length ||
      !accountFormValue;

    return (
      <form className="instance-form" onSubmit={handleSubmit}>
        <LoadingOverlay loading={isFormLoading} />
        <div className="instance-form__body">
          <div className="form-group">
            {/* eslint-disable jsx-a11y/label-has-for */}
            <label>Campaign name *</label>
            <Field
              maxLength="255"
              onBlur={handleBlur}
              name="name"
              component={RenderFormInput}
              type="text"
              validate={[requiredValidator]}
            />

            {/* eslint-disable jsx-a11y/label-has-for */}
            <label>Account *</label>
            <Field
              name="account"
              onBlur={handleBlur}
              validate={[requiredOptionValidator]}
              component={RenderSelectInput}
              getOptions={this.handleGetOptions}
              handleChange={this.handleAccountChange}
              disabled
            />

            <Can I="see" a="Manager">
              {/* eslint-disable jsx-a11y/label-has-for */}
              <label>Campaign manager *</label>
              <Field
                onBlur={handleBlur}
                name="campaign_manager"
                validate={[requiredOptionValidator]}
                component={propsCus => (
                  <React.Fragment>
                    <Select
                      isLoading={isManagersLoading}
                      value={propsCus.input.value}
                      onChange={propsCus.input.onChange}
                      onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                      options={campaignManagerOptions || []}
                    />
                    {propsCus.meta.touched &&
                      propsCus.meta.error && <Error error={propsCus.meta.error} />}
                  </React.Fragment>
                )}
              />
            </Can>
          </div>

          <span className={classes.sendingEmailSubTitle}>Sending emails</span>
          <div className={classes.addEmailWrapper}>
            <Button
              style={{ width: '100%' }}
              size="small"
              variant="outlinedSecondaryDashed"
              startIcon={<PlusIcon />}
              onClick={this.handleEmailConfigAdd}
              disabled={disabledEmailConfigAdd}
            >Add email
            </Button>
            <Counter
              value={!isEmpty(emailConfigsFormValue) ? emailConfigsFormValue.length : 0}
              max={formattedEmailConfigs.length}
            />
          </div>

          {!isEmpty(emailConfigsFormValue) && emailConfigsFormValue.map((emailConfig, index) => {
            const maxEmailAllowedPerDay = this.getMaxEmailAllowedPerDay(emailConfig);
            return (
              <div className={classNames('form-group secondary', classes.emailServiceProviderGroup)}>
                {canRemoveEmailConfig && (
                <button
                  className={classes.deleteEmailButton}
                  type="button"
                  onClick={() => this.handleEmailConfigRemove(index)}
                ><MinusIcon size={16} />
                </button>
                )}
                {/* eslint-disable jsx-a11y/label-has-for */}
                <label>Email to send from *</label>
                <Field
                  onBlur={handleBlur}
                  validate={[requiredOptionValidator]}
                  component={propsCus => (
                    <React.Fragment>
                      <Select
                        hideSelectedOptions // don't show single selected item (toggled by default when isMulti)
                        isLoading={isEmailConfigsLoading}
                        isDisabled={!accountFormValue}
                        placeholder={emailConfigPlaceholder}
                        value={{
                          value: emailConfig.value,
                          label: emailConfig.label,
                        }}
                        onChange={this.handleEmailConfigChange(index)}
                        onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
                        options={availableEmailConfigs}
                        creatable
                        handleCreate={() => this.handleEmailConfigCreate()}
                      />
                      {propsCus.meta.touched &&
                        propsCus.meta.error && <Error error={propsCus.meta.error} />}
                    </React.Fragment>
                )}
                />
                <Grid container spacing={2}>
                  <Grid item sm={6}>
                    {/* eslint-disable jsx-a11y/label-has-for */}
                    <label>Total number of emails per day <br />(Max {maxEmailAllowedPerDay})</label>
                    <Field
                      name={`${emailConfig.value}.total_number_of_emails_per_day`}
                      component={RenderFormInput}
                      disabled={isEmpty(emailConfigsFormValue)}
                      type="number"
                      step="1"
                      min="0"
                      validate={[this.campaignMaxEmailsPerDayValidator(emailConfig)]}
                    />
                  </Grid>
                  <Grid item sm={6}>
                    {/* eslint-disable jsx-a11y/label-has-for */}
                    <label>Delay between each email sent <br />(seconds)</label>
                    <Field
                      maxLength="255"
                      name={`${emailConfig.value}.delay_between_each_email_sent`}
                      component={RenderFormInput}
                      disabled={isEmpty(emailConfigsFormValue)}
                      type="number"
                      step="1"
                      min={MIN_DELAY_BETWEEN_EMAILS}
                    />
                  </Grid>
                </Grid>
              </div>
            );
          })}

        </div>
        <DialogActions className="ml-2 mr-2">
          <Button color="secondary" variant="text" onClick={toggleModal}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            className="ml-2"
            disabled={pristine || submitting || isFormLoading}
            type="submit"
          >
            Save
          </Button>
        </DialogActions>
      </form>
    );
  }
}

SettingsForm.contextType = AbilityContext;

export default compose(
  withStyles(styles),
  withRouter,
  connect(
    state => ({
      accountFormValue: selector(state, 'account'),
      emailConfigsFormValue: selector(state, 'email_service_provider_configs'),
      accounts: state.accounts,
      users: state.users,
      accountEmailConfigs: state.accountEmailConfigs,
      formattedEmailConfigs: accountEmailConfigsSelector(state.accountEmailConfigs),
      emailConfigs: [
        ...state.accountEmailConfigs.results,
        ...state.campaign.email_service_provider_configs,
      ],
      campaign: state.campaign,
    }),
    {
      getAccounts, getAccountEmailConfigs, getUsers, setAddEmailConfigOpen,
    },
  ),
  reduxForm({
    form: 'settingsForm',
  }),
)(SettingsForm);
